import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { AvailabilityRow, DriverApi } from "../driver-api.ts";
import { OFFLINE_BY_REQUEST } from "../driver-api.ts";
import {
  blockingDocument,
  checkRows,
  expiringDocument,
  readinessChip,
  screenFor,
  type LocationPermission,
  type ReadinessScreen,
  type ReadinessView,
} from "../shift-readiness.ts";
import { urlForPage } from "../page-location.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { HeroDisc } from "./Hero.tsx";
import { PhoneShell, type ActionProps } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * The three readiness screens, from one read of EXP-003's `shift-readiness`.
 *
 *   - `1 Go online` — _"Ready to go online?"_ / _"All four must pass before you enter the dispatch
 *     pool."_ Four check rows and one slide-to-confirm.
 *   - `1b Can't go online` — _"Your PrDP has expired"_ / _"You can't go online until a valid permit
 *     is approved."_ Three check rows and one action.
 *   - `7 Document expiring` — an amber banner, a schematic map, _"Finish your jobs, then renew"_ /
 *     _"At 23:59 you'll go offline automatically. Current jobs can finish."_ and two actions.
 *
 * Which one is drawn is the readiness answer's to decide and is decided in `shift-readiness.ts`,
 * away from here. What this component does is fetch, render and act — and the one judgement it makes
 * of its own is that a courier who asked for a screen the answer does not put them on is shown the
 * answer's screen instead, and the address is corrected to match. The alternative is a Go online
 * button over a blocked verdict, which is a button that fails.
 */

/**
 * `Upload renewed permit` can be pressed now, and what it took.
 *
 * This constant was a refusal, and it had been right twice over. The first version said nothing
 * published accepts an upload, which stopped being true when
 * [ADR 0025](../../../docs/adr/0025-the-upload-service-gets-a-public-surface-and-its-own-device-scope.md)
 * published FIL-02 on `/v1` behind `uploads:self`. The second said the upload could not be
 * **attached**, because a permit is a DRV-03 document and
 * `PUT /v1/drivers/{driverId}/documents/{documentId}` sits behind `driver-document:write` — "a
 * scope no channel client grants".
 *
 * That is now false too, and it was made false the way ADR 0032 says: `offline-queue.ts` spends
 * the scope when it attaches a completed upload, `channel-scopes.mjs` read this application's own
 * AST, and the realm went from 30 scopes to 31. Nothing was hand-granted.
 *
 * So the button navigates to the capture screen, and the refusal is kept for the one case that is
 * still real: a token that does not carry the scope. A courier signed in before the realm was
 * reconciled holds the old grant until their next sign-in, and `6 Offline queue` already reads
 * scopes the same way for the same reason.
 */
const UPLOAD_PERMIT_UNAVAILABLE =
  "This sign-in does not carry driver-document:write, so a capture cannot be attached to your permit record yet. Signing out and in again takes the grant the realm now holds.";

/** The scope the capture screen's attach spends. Named here so the refusal can name it. */
const ATTACH_SCOPE = "driver-document:write";
export interface GoOnlineProps {
  readonly api: DriverApi;
  readonly tenantId: string;
  /** Absent when the token carries no `driver_id`. The screen then says so rather than guessing. */
  readonly driverId: string | undefined;
  /** Which of the three the address asked for, so a correction can be noticed. */
  readonly asked: ReadinessScreen;
  readonly onScreenChange: (screen: ReadinessScreen) => void;
  readonly scopes: ReadonlySet<string>;
  /**
   * DRV-04's identifier for this courier's vehicle, where a deployment can supply one.
   *
   * Nothing on this platform joins a courier to a vehicle — DRV-04 publishes no listing and carries
   * no `driverId` — so EXP-003 takes it as a query parameter and reports `vehicle_not_joinable`
   * without it. This application has nowhere to get one either, so it is a prop rather than
   * something invented, and the vehicle row is drawn as the absence. R-22.
   */
  readonly vehicleId?: string;
  /** GEO-06's availability query requires a `productRef` and no driver record publishes one. */
  readonly productRef?: string;
  /** Whether the browser will give this page a position. The fourth check row's whole subject. */
  readonly locationPermission: LocationPermission;
  readonly now: () => number;
}

export function GoOnline(props: GoOnlineProps): ReactNode {
  const [view, setView] = useState<ReadinessView | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  const [availability, setAvailability] = useState<AvailabilityRow | undefined>(undefined);
  const [commandState, setCommandState] = useState<ViewState | undefined>(undefined);

  const { api, tenantId, driverId, vehicleId, productRef, scopes } = props;

  /*
   * The courier's current vehicle, read before readiness is asked for.
   *
   * EXP-003 evaluates the vehicle gate against a `vehicleId` its caller supplies and reports
   * `vehicle_not_joinable` without one, and nothing was supplying one: `vehicleId` is a prop of
   * this screen and no caller passed it, so the gate has reported `unknown` on every load since
   * the screen was built.
   *
   * It was supplying nothing because three docblocks — one here, one in EXP-003's application layer
   * and one in its domain — said there was nothing to supply: "DRV-04 serves one read by
   * `vehicleId`, publishes no listing, and carries no `driverId` on the record". That was true when
   * it was written. DRV-04 now publishes `GET /v1/drivers/{driverId}/vehicle-assignments` — "the
   * vehicles assigned to one driver, newest first", in its own summary, whose description names
   * this very screen as its reader — and this application has been calling it for the Vehicle
   * screen all along. The claim outlived the gap it described, and the screen went on drawing an
   * absence it no longer had.
   *
   * The newest **active** assignment, because `status` is `active` or `ended` and an ended
   * assignment is a vehicle this courier used to drive. No assignment is not a failure: the gate
   * then says `vehicle_absent`, which is the true answer for a courier with no vehicle, and is a
   * different thing from `vehicle_not_joinable` — the platform cannot answer versus the platform
   * answering no.
   */
  const resolveVehicle = useCallback(async (): Promise<string | undefined> => {
    if (vehicleId !== undefined) return vehicleId;
    if (driverId === undefined) return undefined;
    try {
      const page = await api.vehicleAssignments(driverId);
      const current = page.assignments.find((assignment) => assignment.status === "active");
      return current?.vehicle.vehicleId;
    } catch {
      /*
       * Swallowed deliberately. A failed assignment read must not fail the readiness screen: the
       * other four gates are still answerable, and losing them all to one absent vehicle is the
       * over-claim in reverse. Readiness proceeds without a vehicle and the gate reports that it
       * could not be joined, which is what it reported before this read existed.
       */
      return undefined;
    }
  }, [api, driverId, vehicleId]);

  const load = useCallback(() => {
    if (driverId === undefined) return;
    setState({ kind: "loading" });
    resolveVehicle()
      .then((resolvedVehicleId) =>
        api.shiftReadiness({
          tenantId,
          driverId,
          ...(resolvedVehicleId === undefined ? {} : { vehicleId: resolvedVehicleId }),
          ...(productRef === undefined ? {} : { productRef }),
        }),
      )
      .then((answer) => {
        setView(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => {
        setState(stateForError(error, ["driver-app:read"]));
      });
  }, [api, tenantId, driverId, productRef, resolveVehicle]);

  useEffect(load, [load]);

  /*
   * DRV-05's row, for the `expectedVersion` the Go online command requires.
   *
   * EXP-003 publishes the presence *gate* and not DRV-05's version, so this is a direct read of the
   * resource the command then writes — recorded in `driver-api.ts` and in R-29. Fetched alongside
   * the readiness answer rather than on the press, so a courier's slide does not wait on a round
   * trip; refetched after every command, because the version has moved.
   */
  const loadAvailability = useCallback(() => {
    if (driverId === undefined) return;
    api
      .currentAvailability(driverId)
      .then(setAvailability)
      // Silent: the readiness answer already carries the presence gate, which is what the screen
      // draws. This read exists only so a command has a version, and a command without one says so
      // when it is pressed rather than reporting an error over a screen that is otherwise correct.
      .catch(() => setAvailability(undefined));
  }, [api, driverId]);

  useEffect(loadAvailability, [loadAvailability]);

  /*
   * The two heartbeats this screen used to run are in `shift-heartbeat.ts`, mounted by the shell.
   *
   * They were here, and here is where a courier stops looking: the screen a courier waits for work
   * on is the offer inbox, and this component unmounts the moment they open it. So the lease died
   * on exactly the screen that mattered. Measured on the deployment — a courier went online, opened
   * their inbox, a rider booked ninety seconds later, and the dispatch failed `no_supply` on all
   * three attempts against an availability row 167 seconds old.
   *
   * A shift belongs to the session rather than to a screen, which is where it lives now.
   */

  if (driverId === undefined) {
    /*
     * The token carried no `driver_id`.
     *
     * Not an error and not an empty screen: nothing on this platform states that a token's subject
     * is a courier's `driverId` — DRV-01 mints one with `randomUUID()` and keeps it separate — so
     * sending the subject would read a different courier's readiness on any realm where the two
     * differ. R-10, pinned in `docs/OPEN_WORK.md`.
     */
    return (
      <StateScreen
        screen="readiness"
        chip={{ class: "unknown", label: "no courier id" }}
        title="We don't know which courier you are"
        sub="Your sign-in carries no courier identifier, so there is nothing to look up. Whoever administers your account sets that."
        flag="wording not in the design"
      />
    );
  }

  if (state.kind !== "ready" || view === undefined) {
    return (
      <StateScreen
        screen="readiness"
        state={state}
        onRetry={load}
        purpose="Checking whether you can go online."
      />
    );
  }

  const screen = screenFor(view);
  /*
   * The address follows the answer.
   *
   * Reported upwards rather than pushed from here, so there is one place that owns the address bar.
   * A courier who bookmarked `?page=go-online` and whose permit has since expired lands on
   * `?page=cannot-go-online`, which is the screen the design draws for them.
   */
  if (screen !== props.asked) props.onScreenChange(screen);

  const rows = checkRows(view, props.locationPermission);
  const chip = readinessChip(view);
  const online = view.gates.find((gate) => gate.gate === "presence")?.verdict === "pass";

  const goOnline = (): void => {
    if (availability === undefined) {
      setCommandState({
        kind: "error",
        code: "no_expected_version",
        message:
          "DRV-05 did not say which version of your availability this would replace, so going online cannot be attempted safely.",
        retryable: true,
      });
      return;
    }
    setCommandState({ kind: "loading" });
    api
      .goOnline(driverId, availability.version)
      .then(async (row) => {
        /*
         * Going online is two commands, not one.
         *
         * DRV-05 separates presence from availability — a courier is `online` while carrying a
         * passenger and `unavailable` at the same time — so `goOnline` always lands on
         * `availability: unavailable` with `AWAITING_AVAILABILITY` and issues the lease. Nothing
         * made the second move, and a provisioned courier sat at `online / unavailable` for as
         * long as anyone watched, invisible to dispatch.
         *
         * The row this command returned carries the version and the lease the next one needs, and
         * a lapsed lease is refused, so it is used rather than re-read.
         */
        if (row.leaseId !== undefined && row.leaseId !== null) {
          await api.setAvailable({
            driverId,
            expectedVersion: row.version,
            leaseId: row.leaseId,
          });
        }
        setCommandState(undefined);
        load();
        loadAvailability();
      })
      .catch((error: unknown) => {
        setCommandState(stateForError(error, ["driver-availability:write"]));
        // The version has moved whether the command succeeded or conflicted, so it is re-read either
        // way. A 409 that left the old version in place would make the next press fail identically.
        loadAvailability();
      });
  };

  const goOffline = (): void => {
    const leaseId = availability?.leaseId;
    if (availability === undefined || leaseId === undefined) {
      setCommandState({
        kind: "error",
        code: "no_lease",
        message:
          "DRV-05 published no lease for you, and its offline command requires one. Nothing was changed.",
        retryable: true,
      });
      return;
    }
    setCommandState({ kind: "loading" });
    api
      .goOffline({
        driverId,
        expectedVersion: availability.version,
        leaseId,
        reasonCode: OFFLINE_BY_REQUEST,
      })
      .then(() => {
        setCommandState(undefined);
        load();
        loadAvailability();
      })
      .catch((error: unknown) => {
        setCommandState(stateForError(error, ["driver-availability:write"]));
        loadAvailability();
      });
  };

  if (commandState !== undefined && commandState.kind !== "ready") {
    return (
      <StateScreen
        screen="readiness"
        state={commandState}
        onRetry={() => setCommandState(undefined)}
        purpose="Changing whether you are online."
      />
    );
  }

  const canWrite = scopes.has("driver-availability:write");
  /*
   * Going online needs two scopes, not one, and the second one's absence is invisible.
   *
   * `driver-availability:write` opens the shift. `driver-availability:heartbeat` keeps it: DRV-05's
   * lease is sixty seconds, and DSP-01 bounds `availabilityAge` at the same sixty, so a courier
   * whose sign-in cannot extend the lease goes online, looks online to a dispatcher, and is filtered
   * out of every ranking a minute later. The heartbeat is a background call that deliberately
   * swallows its errors, which is right for a lost request and wrong for a missing scope — a lost
   * request is retried in twenty-five seconds and a missing scope never resolves.
   *
   * So it is said on the control before the shift starts, rather than discovered as an absence of
   * work. Naming the scope here is also what grants it: `channel-scopes.mjs` derives this client's
   * grant from the published scope names its own source mentions.
   */
  const canHeartbeat = scopes.has("driver-availability:heartbeat");

  if (screen === "cannot-go-online") return blockedScreen();
  if (screen === "document-expiring") return expiringScreen();
  return readyScreen();

  /* ------------------------------------------------------------------ 1 Go online */

  function readyScreen(): ReactNode {
    const action: ActionProps = {
      label: "Go online",
      glyph: "power_settings_new",
      primary: true,
      slide: true,
      ...(canWrite && canHeartbeat
        ? { onPress: online ? goOffline : goOnline }
        : {
            unavailable: `Your sign-in does not carry ${
              canWrite ? "driver-availability:heartbeat" : "driver-availability:write"
            }.`,
          }),
    };
    return (
      <PhoneShell
        screen="readiness"
        art={<HeroDisc glyph="power_settings_new" caption={online ? "Online" : "Offline"} />}
        online={online}
        earningsLabel={null}
        chip={chip}
        title="Ready to go online?"
        sub="All four must pass before you enter the dispatch pool."
        checks={rows}
        actions={[action]}
        {...(view!.readyToGoOnline === "unknown"
          ? {
              /*
               * The verdict is `unknown` and the design draws no word for that.
               *
               * The chip is already the design's dashed `unknown` pill, which says a state is not
               * confirmed without saying it is either of the other two. This line says which gate.
               */
              flag: unconfirmedGates(view!),
            }
          : {})}
      />
    );
  }

  /* --------------------------------------------------------------- 1b Can't go online */

  function blockedScreen(): ReactNode {
    const document = blockingDocument(view!);
    const blockedGate = view!.gates.find((gate) => gate.verdict === "blocked");
    /*
     * `Your PrDP has expired` — the design's headline, with the document named from DRV-03.
     *
     * `PrDP` is a `type` value and is printed exactly as DRV-03 published it rather than prettified:
     * a document kind this build has never heard of is a kind whose name it must not rewrite.
     *
     * A `blocked` verdict that is **not** a document still lands on this screen, because it is the
     * only one of the three that draws a refusal — and its headline then names what actually
     * blocked. A Go online button over a suspended profile is a button that fails.
     */
    const headline =
      document !== undefined
        ? document.expired
          ? `Your ${document.type} has expired`
          : `Your ${document.type} is not approved yet`
        : "You can't go online yet";
    const sub =
      document !== undefined
        ? "You can't go online until a valid permit is approved."
        : blockedReason(blockedGate?.gate);

    return (
      <PhoneShell
        screen="readiness"
        art={<HeroDisc glyph="power_settings_new" caption="Offline" />}
        online={online}
        earningsLabel={null}
        chip={chip}
        title={headline}
        sub={sub}
        // Three rows, as the design draws: the documents, the vehicle and the service area. The
        // location row is screen 1's and this screen does not draw it.
        checks={rows.slice(0, 3)}
        actions={[
          {
            label: "Upload renewed permit",
            glyph: "upload",
            primary: true,
            /*
             * Pressable when the token can attach, refused by name when it cannot. The press goes
             * to the capture screen rather than uploading anything from here: the design draws the
             * capture on `Document upload`, and the attach happens where the queue already
             * survives a reload.
             */
            ...(scopes.has(ATTACH_SCOPE)
              ? {
                  onPress: (): void => {
                    window.location.assign(urlForPage(window.location.href, "document-upload"));
                  },
                }
              : { unavailable: UPLOAD_PERMIT_UNAVAILABLE }),
          },
        ]}
        {...(document === undefined ? { flag: "wording not in the design" } : {})}
      />
    );
  }

  /* ----------------------------------------------------------- 7 Document expiring */

  function expiringScreen(): ReactNode {
    const document = expiringDocument(view!);
    /*
     * `PrDP expires today at 23:59` and `At 23:59 you'll go offline automatically`.
     *
     * The hour is DRV-03's own `expiresOn`, formatted on the device's clock in 24-hour time, which
     * is what the design prints. No tenant timezone is published anywhere on this platform, so the
     * device's is the only one available and a courier's phone is set to where they are working.
     * Recorded as Q-65.
     *
     * `today` is checked rather than asserted: the design's sample expires today and
     * `expiringSoon`'s window is configuration, so a document expiring tomorrow would have made the
     * design's word wrong.
     */
    const at = document?.expiringAt === null ? undefined : document?.expiringAt;
    const clock = at === undefined ? undefined : timeOfDay(at);
    const sameDay = at !== undefined && isSameLocalDay(at, view!.observedAt);
    const when = clock === undefined ? undefined : sameDay ? `today at ${clock}` : dayAndTime(at!);

    return (
      <PhoneShell
        screen="readiness"
        map
        online={online}
        earningsLabel={null}
        chip={chip}
        {...(when === undefined || document === undefined
          ? {}
          : { banner: { glyph: "schedule", text: `${document.type} expires ${when}` } })}
        title="Finish your jobs, then renew"
        sub={
          clock === undefined
            ? "You'll go offline automatically when it expires. Current jobs can finish."
            : `At ${clock} you'll go offline automatically. Current jobs can finish.`
        }
        actions={[
          {
            label: "Upload renewed permit",
            glyph: "upload",
            primary: true,
            unavailable: UPLOAD_PERMIT_UNAVAILABLE,
          },
          {
            label: "Remind me later",
            glyph: "schedule",
            primary: false,
            unavailable:
              "Nothing publishes a deferral: no reminder.deferredUntil and no notification.snoozedUntil.",
          },
        ]}
      />
    );
  }
}

/**
 * Which gates could not be answered, for the line under an `unknown` verdict.
 *
 * Named rather than counted. "Two gates unconfirmed" sends a courier nowhere; "vehicle, service
 * area" tells them which two, and both of those have a recorded reason the platform published.
 */
function unconfirmedGates(view: ReadinessView): string {
  const names = view.gates
    .filter((gate) => gate.verdict === "unknown")
    .map((gate) => gate.gate)
    .join(", ");
  return names.length === 0 ? "wording not in the design" : `not confirmed: ${names}`;
}

/** Why a courier is blocked when it is not a document, in words the design does not supply. */
function blockedReason(gate: string | undefined): string {
  switch (gate) {
    case "profile":
      return "Your courier record is not active, so you can't enter the dispatch pool.";
    case "vehicle":
      return "Your vehicle isn't approved, so you can't enter the dispatch pool.";
    case "presence":
      return "Dispatch is holding you back right now. The reason is on the row below.";
    case "serviceArea":
      return "You're outside the area this service covers.";
    default:
      return "Something below has to pass before you can enter the dispatch pool.";
  }
}

/** `23:59`, on the device's clock, in the 24-hour form the design prints. */
export function timeOfDay(instant: string): string | undefined {
  const parsed = new Date(instant);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return `${String(parsed.getHours()).padStart(2, "0")}:${String(parsed.getMinutes()).padStart(2, "0")}`;
}

/** `14 Nov at 23:59`, for an expiry the design's word "today" would have described wrongly. */
export function dayAndTime(instant: string): string | undefined {
  const parsed = new Date(instant);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `on ${String(parsed.getDate())} ${months[parsed.getMonth()]!} at ${timeOfDay(instant) ?? ""}`;
}

/** Whether two instants fall on the same day on the device's clock. */
export function isSameLocalDay(left: string, right: string): boolean {
  const a = new Date(left);
  const b = new Date(right);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
